import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext } from 'react';
import { useAlternatePaymentMethodsMutation } from '@syf-api-library/alternate-payment-methods';
import { usePaymentMethodsMutation } from '@syf-api-library/payment-methods';
import Textfield from 'syf-component-library/ui/atoms/Textfield';
import { Grid } from 'syf-component-library/ui/patterns';
import { Inline, Stack } from 'syf-component-library/ui/spacing';
import getLanguageFromStorage from 'syf-js-utilities/helpers/getLanguageFromStorage';
import { PAGE_FUNCTION, PAGE_NAME, PaymentMethodToPageSubFunctionText, SUB_FUNCTION } from '../../../../const/analytics';
import { discoverCardMiniImg, masterCardMiniImg, securityCodeImage, visaCardMiniImg } from '../../../../const/assets';
import MainContext from '../../../../Context';
import { isPayLaterProduct } from '../../../../helpers';
import { getErrorMessage, getPaymentTypeError } from '../../../../helpers/getPaymentMethodErrorMessage';
import isDCHEligible from '../../../../helpers/isDCHEligible';
import { useApiConfig } from '../../../../hooks';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import usePaymentMethodMachine from '../../../../hooks/machines/usePaymentMethodMachine';
import { CARD_NUMBER_INPUT_NAME } from '../../../../machines/paymentMethod/cardForm/cardNumberConfig';
import { CVV2_INPUT_NAME } from '../../../../machines/paymentMethod/cardForm/cvv2Config';
import { EXPIRATION_DATE_INPUT_NAME } from '../../../../machines/paymentMethod/cardForm/expirationDateConfig';
import { FULLNAME_INPUT_NAME } from '../../../../machines/paymentMethod/cardForm/fullNameConfig';
import { t } from '../../../../strings/i18n';
import { useField, useXForm, XForm } from '../../../../xform';
import { ErrorIcon } from '../../../atoms/ErrorIcon';
import ImageForCardNumberInput from '../../../atoms/ImageForCardNumberInput';
import { MiniCards, MiniCardsWrapper } from '../../../atoms/MiniCard';
import { Image, ImageContainer } from '../../../atoms/styled';
const Form = () => {
    const { submit } = useXForm();
    const fullName = useField(FULLNAME_INPUT_NAME);
    const cardNumber = useField(CARD_NUMBER_INPUT_NAME);
    const expirationDate = useField(EXPIRATION_DATE_INPUT_NAME);
    const cvv2 = useField(CVV2_INPUT_NAME);
    const handleSubmit = (e) => {
        e.preventDefault();
        submit();
    };
    return (_jsxs("form", Object.assign({ onSubmit: handleSubmit }, { children: [_jsx("span", { "data-scroll": FULLNAME_INPUT_NAME }), _jsx(Textfield, { "aria-required": true, "data-private": true, dataCy: "card-full-name", error: fullName.error, errorIcon: _jsx(ErrorIcon, {}), id: "fullName", name: "fullName", placeholder: t('addPaymentMethod.fullName'), type: "text", value: fullName.value, width: "100%", onBlur: fullName.blur, onChange: (e) => fullName.setValue(e.target.value) }), _jsx(Stack, { size: "large" }), _jsx("span", { "data-scroll": CARD_NUMBER_INPUT_NAME }), _jsx(Textfield, { "aria-required": true, "data-private": true, dataCy: "card-number", error: cardNumber.error, errorIcon: _jsx(ErrorIcon, {}), id: "cardNumber", inputMode: "numeric", leftIcon: _jsx(ImageForCardNumberInput, { firstCardNumber: cardNumber.value ? cardNumber.value[0] : undefined }), matchPattern: /^([0-9]*)$/, name: "cardNumber", placeholder: t('addPaymentMethod.cardNumber'), type: "text", value: cardNumber.value, width: "100%", onBlur: cardNumber.blur, onChange: (e) => cardNumber.setValue(e.target.value) }), _jsx(Stack, { size: "large" }), _jsx("span", { "data-scroll": EXPIRATION_DATE_INPUT_NAME }), _jsx(Textfield, { "data-private": true, dataCy: "card-expiration-date", error: expirationDate.error, errorIcon: _jsx(ErrorIcon, {}), helpText: t('addPaymentMethod.expirationHelpText'), id: "expiration-date", inputMode: "numeric", matchPattern: /^([0-9]*)$/, maxLength: 4, name: "expirationDate", placeholder: t('addPaymentMethod.expiration'), type: "text", value: expirationDate.value, width: "100%", onBlur: expirationDate.blur, onChange: (e) => expirationDate.setValue(e.target.value) }), _jsx(Stack, { size: "large" }), _jsx("span", { "data-scroll": CVV2_INPUT_NAME }), _jsx(Textfield, { "data-private": true, dataCy: "card-cvv", error: cvv2.error, errorIcon: _jsx(ErrorIcon, {}), helpText: t('addPaymentMethod.cvvHelpText'), id: "cvv", inputMode: "numeric", matchPattern: /^([0-9]*)$/, name: "cvv", placeholder: t('addPaymentMethod.cvv'), type: "text", value: cvv2.value, width: "100%", onBlur: cvv2.blur, onChange: (e) => cvv2.setValue(e.target.value) }), _jsx("input", { "aria-hidden": true, "data-testid": "submit-button", style: { display: 'none' }, type: "submit" })] })));
};
const DebitCreditAccountForm = ({ className, setFormErrorMessage, setIsSubmitting }) => {
    const { sendToMainMachine, staticHostName, triggerAnalyticsEvent, featureFlags } = useContext(MainContext);
    const [paymentMethodMachine] = usePaymentMethodMachine();
    const [apiMachine] = useApiMachine();
    const [, , sendToPaymentFlowMachine] = usePaymentFlowMachine();
    const { accountSummaryQueryResult } = apiMachine.context;
    const apiConfig = useApiConfig();
    const { data: accountSummary } = accountSummaryQueryResult;
    const productType = accountSummary.product.type;
    const paymentMethodMutation = usePaymentMethodsMutation(apiConfig);
    const alternatePaymentMethodMutation = useAlternatePaymentMethodsMutation(apiConfig);
    // TODO: delete legacyIsDebit when isManagePaymentMethodsEnabled FeatureFlag is deprecated
    const legacyIsDebit = paymentMethodMachine.matches('addPaymentMethod.addNewDebitCard');
    const isDebit = legacyIsDebit || paymentMethodMachine.matches('addNewDebitCard');
    // END TODO
    const isDCHDebitCard = isDCHEligible(accountSummary.billing, productType, featureFlags) && isDebit;
    const mutation = (isPayLaterProduct(productType) &&
        !featureFlags.isPLAddPaymentMethodsEnabled) ||
        isDCHDebitCard
        ? alternatePaymentMethodMutation
        : paymentMethodMutation;
    const paymentMethodType = isDebit ? 'PINLESS_DEBIT_CARD' : 'CREDIT_CARD';
    const onInvalidHandler = (formErrors) => {
        const errorMessage = [
            [formErrors.fullName, 'Full-Name'],
            [formErrors.cardNumber, 'Card-Number'],
            [formErrors.expirationDate, 'Expiration-Date'],
            [formErrors.cvv2, 'Security-Code']
        ]
            .filter(([value]) => Boolean(value))
            .map(([value, key]) => `${key}: ${value}`)
            .join(' ');
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
            pageSubFunction: SUB_FUNCTION.PAYMENT_METHOD.ADD_PAYMENT_METHOD,
            pageName: isDebit
                ? PAGE_NAME.PAYMENT_METHOD.ADD_PAYMENT_METHODS_DEBIT_ERROR
                : PAGE_NAME.PAYMENT_METHOD.ADD_PAYMENT_METHODS_CREDIT_ERROR,
            errorMessage
        });
    };
    // TODO: the `submitHandler` logic in this form should be moved to the payment method machines. It is currently copy and pasted in payment method forms
    const submitHandler = async (paymentCardInfo) => {
        setIsSubmitting(true);
        try {
            const paymentMedium = paymentMethodType;
            const { cardNumber, cvv2, expirationDate } = paymentCardInfo;
            const newPaymentMethod = {
                paymentMedium,
                paymentCardInfo: {
                    'cipher.cardNumber': cardNumber,
                    'cipher.cvv2': cvv2,
                    'cipher.expDate': expirationDate
                }
            };
            const { paymentMethod: { id: paymentMethodId } } = await mutation.mutateAsync(newPaymentMethod);
            sendToPaymentFlowMachine({
                type: 'SET_NEW_PAYMENT_METHOD',
                paymentMethodId
            });
            sendToPaymentFlowMachine({
                type: 'SET_CURRENT_DATE'
            });
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const onSubmitErrorHandler = (error) => {
        const response = error === null || error === void 0 ? void 0 : error.response;
        if (response) {
            getErrorMessage(response, paymentMethodType).then((errorMessage) => {
                setFormErrorMessage(errorMessage);
                const paymentTypeError = getPaymentTypeError(errorMessage);
                let pageName = 'error';
                switch (paymentTypeError) {
                    case 'PINLESS_DEBIT_CARD':
                        pageName = PAGE_NAME.PAYMENT_METHOD.ADD_PAYMENT_METHODS_DEBIT_ERROR;
                        break;
                    case 'CREDIT_CARD':
                        pageName = PAGE_NAME.PAYMENT_METHOD.ADD_PAYMENT_METHODS_CREDIT_ERROR;
                        break;
                    default:
                        break;
                }
                triggerAnalyticsEvent({
                    pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                    pageSubFunction: SUB_FUNCTION.PAYMENT_METHOD.ADD_PAYMENT_METHOD,
                    pageName,
                    errorMessage
                });
            });
        }
    };
    const onSubmitHandler = () => {
        sendToMainMachine({
            type: 'GO_TO_PAYMENT_FLOW'
        });
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
            pageSubFunction: PaymentMethodToPageSubFunctionText[paymentMethodType],
            pageName: PAGE_NAME.COMPLETE
        });
    };
    // Mini cards data
    const miniCards = {
        VISA: {
            ariaHidden: false,
            altText: t('addPaymentMethod.cardTypeAccepted'),
            url: `${staticHostName}${visaCardMiniImg}`,
            id: 1
        },
        MASTERCARD: {
            ariaHidden: true,
            altText: '',
            url: `${staticHostName}${masterCardMiniImg}`,
            id: 2
        },
        DISCOVER: {
            ariaHidden: true,
            altText: '',
            url: `${staticHostName}${discoverCardMiniImg}`,
            id: 3
        }
    };
    return (_jsxs(Grid, Object.assign({ className: className }, { children: [_jsx(MiniCardsWrapper, Object.assign({ item: true, xs: 12 }, { children: _jsx(MiniCards, { miniCards: miniCards }) })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(Stack, { size: "large" }) })), _jsxs(Grid, Object.assign({ item: true, md: 6, xs: 12 }, { children: [_jsx(XForm, Object.assign({ formMachine: paymentMethodMachine.context.form, submit: submitHandler, onInvalid: onInvalidHandler, onSubmit: onSubmitHandler, onSubmitError: onSubmitErrorHandler }, { children: _jsx(Form, {}) })), _jsx(Stack, { size: "xlarge" })] })), _jsxs(ImageContainer, Object.assign({ item: true, md: 6, xs: 12 }, { children: [_jsx(Inline, { hideOnBreakpointDown: "medium" }), _jsxs(Grid, { children: [_jsx(Image, { alt: t('addPaymentMethod.securityCodeImageAlt'), src: `${staticHostName}${securityCodeImage(getLanguageFromStorage())}` }), _jsx(Stack, { hideOnBreakpointUp: "medium", size: "xlarge" })] })] }))] })));
};
export default DebitCreditAccountForm;
